<template>
    <v-sheet class="text-center" height="500px" style="position: relative;">
        
        <div class="close-sheet-container" v-if="!showUsageData">
            <v-btn @click="$emit('close')" icon>
                <v-icon x-large>mdi-arrow-down</v-icon>
            </v-btn>
        </div>


        <div class="usage-button-container">
            <v-btn @click="getUsageData" :color="chatGptThemeColor" v-if="!showUsageData">View Usage</v-btn>
            <v-btn @click="showUsageData = false" :color="chatGptThemeColor" v-if="showUsageData">Close Usage</v-btn>
        </div>


        <div class="getting-usage-data" v-if="gettingUsageData">
            Getting usage data...
            <v-icon>fas fa-spinner fa-spin</v-icon>
        </div>
        <div class="show-usage-area" v-if="showUsageData">
            <div>
                <highcharts :options="usageDataChartOptions"></highcharts>
            </div>
            <div class="usage-totals-container">
                <span class="usage-totals">ANNUAL TOTAL: <b>{{ `$${Math.round((usageData.total_usage / 100) * 100) / 100}` }}</b></span>
            </div>
        </div>


        <div class="upload-file-area" v-if="!fileUploaded && !gettingUsageData && !showUsageData">
            <div class="upload-file-container">
                <template v-if="!uploading">
                    <v-btn color="#c42111" @click="uploadCurrentGridData">Upload current grid data</v-btn>
                    <span class="mt-4 mb-4" style="display: block;">Or</span>

                    <v-file-input dense outlined prepend-icon="" color="#C42111" class="upload-file-control" v-model="selectedFile" accept=".csv" label="" placeholder="Upload a CSV file" :show-size="1024" :rules="[$store.state.validationFuncs.maxFileSize(selectedFile, 5000000, '5MB')]"></v-file-input>
                    <v-tooltip bottom color="rgba(97, 97, 97, 1.0)">
                        <template v-slot:activator="{ on }">
                            <v-btn class="ml-2" style="vertical-align: sub;" color="#c42111" width="20" max-width="20" min-width="20" @click="uploadSelectedFile" v-on="on" :disabled="!selectedFile">
                                <v-icon>mdi-cloud-upload</v-icon>
                            </v-btn>
                        </template>
                        <span>Upload selected file</span>
                    </v-tooltip>
                </template>

                <template v-else>
                    <v-progress-circular color="#ffffff" rotate="-90" size="100" width="8" :value="uploadProgress">{{ uploadProgress }}</v-progress-circular>
                </template>
            </div>
        </div>


        <template v-if="fileUploaded && !showUsageData">
            <div class="main-message-area">

                <div class="extra-buttons-container">
                    <v-menu offset-x nudge-right="5" nudge-bottom="0">
                        <template v-slot:activator="{ on: onMenu, attrs }">
                            <v-tooltip bottom v-on="onMenu" color="rgba(97, 97, 97, 1.0)">
                                <template #activator="{ on: onTooltip }">
                                    <v-btn style="display: block;" color="#747474" width="30" min-width="30" height="30" v-bind="attrs" elevation="1" v-on="{ ...onMenu, ...onTooltip }">
                                        <v-icon color="#ffffff">mdi-content-save-move</v-icon>
                                    </v-btn>
                                </template>
                                <span>Saved Prompts</span>
                            </v-tooltip>
                        </template>

                        <v-card class="mx-auto" max-width="500" elevation="5" style="border: solid 1px #747474;">
                            <v-toolbar color="#747474" dark height="40" elevation="0">
                                <v-toolbar-title>Saved Prompts</v-toolbar-title>
                                <v-spacer></v-spacer>
                                <!-- <v-btn icon><v-icon>mdi-magnify</v-icon></v-btn> -->
                            </v-toolbar>
                            <v-list style="padding: 0px; overflow-y: auto;" max-height="200">
                                <template v-for="(savedPrompt, idx) in savedPrompts">
                                    <v-list-item class="saved-prompt-item" :key="savedPrompt.prompt_id" @click="loadPrompt(savedPrompt)">
                                        <v-list-item-content>
                                            <v-list-item-subtitle style="font-size: 12px;" class="text-left">{{ formatTS(savedPrompt.message_ts) }}</v-list-item-subtitle>
                                            <v-list-item-subtitle style="color: #ffffff; font-size: 14px;" class="text-left">{{ savedPrompt.prompt_text }}</v-list-item-subtitle>

                                            <v-btn class="delete-prompt-item-button" elevation="0" width="20" min-width="20" height="30" @click.stop="deletePromptClick(savedPrompt)">
                                                <v-icon small color="red">mdi-delete-forever</v-icon>
                                            </v-btn>

                                        </v-list-item-content>
                                    </v-list-item>
                                    <v-divider v-if="idx < savedPrompts.length - 1" />
                                </template>

                                <v-list-item-subtitle style="color: #ffffff; font-size: 14px;" class="text-left pa-4" v-if="savedPrompts.length == 0">
                                    You do not have any saved prompts
                                </v-list-item-subtitle>

                            </v-list>
                        </v-card>

                    </v-menu>
                </div>


                <div class="messages-area">
                    <div class="message-block" v-for="(message, index) in messages" :key="index">
                        <v-divider :inset="false" v-if="index"></v-divider>
                        <v-list-item>
                            <v-list-item-avatar style="align-self: start; top: 12px;">
                                <img class="user-image" v-if="message.userType == 'user' && $store.state.userImage" :src="$store.state.userImage" />
                                <v-icon class="user-icon" v-else-if="message.userType == 'user'">fa fa-user-circle</v-icon>
                                <v-icon class="user-icon" v-else>mdi-lightning-bolt</v-icon>
                            </v-list-item-avatar>

                            <v-list-item-content>
                                <v-list-item-title class="text-left">{{ message.user }}</v-list-item-title>
                                <v-list-item-subtitle class="text-left">{{ formatTS(message.ts) }}</v-list-item-subtitle>


                                <div class="expired-message-tag" v-if="message.expired">
                                    <span>expired</span>
                                </div>
                                <div class="cancelled-message-tag" v-else-if="message.cancelled">
                                </div>
                                
                                <div v-else>
                                    <span class="text-left d-block" v-if="message.userType == 'user'">{{ message.text }}</span>
                                    <template v-else>

                                        <div v-for="(part, partIndex) in message.parts" :key="partIndex">
                                            <span class="text-left d-block" v-if="part.text" v-html="formatTextForWeb(part.text)" />

                                            <span class="text-center d-block" v-if="part.file">
                                                <v-btn :color="chatGptThemeColor" @click="downloadFile(part.file)">Download</v-btn>
                                            </span>

                                            <div class="image-content" v-if="part.image">
                                                <div class="image-container" :id="part.image.file_id">
                                                    {{ renderImage(part.image) }}
                                                </div>
                                            </div>
                                        </div>

                                    </template>
                                </div>

                            </v-list-item-content>

                            <div class="message-options">
                                <v-btn v-if="message.userType == 'user'" @click="savePromptClick(message)" class="save-prompt-button" style="cursor: pointer;" elevation="0" width="30" min-width="30" height="30"><v-icon small color="#ffffff">mdi-content-save</v-icon></v-btn>
                            </div>
                        </v-list-item>
                    </div>
                </div>
            </div>

            <div class="prompt-input-area">
                <div class="prompt-input">
                    <v-text-field color="#c42111" id="prompt-text-input" outlined placeholder="Message AI..." v-model="promptText" @keyup.enter.native="sendMessage" :disabled="runningRequest">
                        <template v-slot:append>

                            <v-tooltip bottom color="rgba(97, 97, 97, 1.0)">
                                <template v-slot:activator="{ on }">
                                    <v-btn class="mt-1" color="#c42111" width="20" max-width="20" min-width="20" @click="sendMessage" :disabled="!promptText || runningRequest" v-on="on">
                                        <v-icon>mdi-arrow-up-bold</v-icon>
                                    </v-btn>
                                </template>
                                <span>Send Message</span>
                            </v-tooltip>

                        </template>
                    </v-text-field>
                </div>
            </div>
            
            <div class="running-request-indicator-area" v-if="runningRequest">
                <div class="loader">
                    <div class="inner one"></div>
                    <div class="inner two"></div>
                    <div class="inner three"></div>
                </div>
            </div>

            <div class="disclaimer-text-container">
                <span>This is a beta feature. The results may not be fully correct.</span>
            </div>
        </template>

    </v-sheet>

</template>

<script>
export default {
    name: 'AIAnalyzer',
    props: ['context', 'gridApi'],
    components: {

    },
    data: () => ({
        selectedFile: null,
        uploading: false,
        uploadProgress: 0,
        fileUploaded: false,
        threads: [],
        selectedThread: {
            //assistant_id: 'asst_2GwFevmxHS9OLaIdf88Il22k',
            assistant_id: null,
            file_id: null,
            thread_id: null
        },

        promptText: "",
        runningRequest: false,
        messages: [],
        savedPrompts: [],
        gettingImageId: null,
        chatGptThemeColor: '#19c37d',

        usageData: null,
        gettingUsageData: false,
        usageDataChartOptions: null,
        showUsageData: false
    }),

    computed: {

    },

    methods: {
        getSavedPrompts: function() {
            let route = (TELSA.apiHost + 'ai/prompt/list');
            this.$http.post(route, {args: {context: this.$props.context}}).then(response => {
                let results = response.body;
                console.log('getSavedPrompts: ', results);
                this.savedPrompts = results;
            });
        },

        loadFile: function(formData) {
            let self = this;
            //let route = `${TELSA.apiHost}ai/reports/file`;
            let route = `${TELSA.apiHost}ai/file`;
            const xhr = new XMLHttpRequest();
            
            // Set up event listeners to track upload progress
            xhr.upload.addEventListener('progress', function(event) {
                if (event.lengthComputable) {
                    // Calculate percentage of upload completion
                    let percentComplete = ((event.loaded / event.total) * 100).toFixed(2);
                    console.log(`Upload progress: ${percentComplete}%`);
                    self.uploadProgress = percentComplete;
                }
            });

            // Set up event listener for when the upload is complete
            xhr.addEventListener('load', function() {
                console.log('Upload complete');
                self.fileUploaded = true;
                self.uploading = false;
                self.selectedThread.file_id = JSON.parse(xhr.response);
                console.log(xhr);
            });

            // Set up event listener for handling errors
            xhr.addEventListener('error', function() {
                console.error('Upload failed');
                self.uploading = false;
            });

            // Open a POST request to the specified endpoint
            xhr.open('POST', route);
            xhr.setRequestHeader('Authorization', ('Bearer ' + localStorage.getItem('access_token')));
            this.uploading = true;
            xhr.send(formData);
        },

        uploadCurrentGridData: function() {
            let csvData = this.$props.gridApi.getDataAsCsv();
            let csvBlob = new Blob([csvData], { type: 'text/csv' });
            let formData = new FormData();
            formData.append('csvFile', csvBlob, 'data.csv');
            this.loadFile(formData);
        },

        uploadSelectedFile: function() {
            let csvBlob = new Blob([this.selectedFile], { type: 'text/csv' });
            let formData = new FormData();
            formData.append('csvFile', csvBlob, this.selectedFile.name);
            this.loadFile(formData);
        },

        sendMessage: function() {
            console.log(`Send message: ${this.promptText}`);
            this.runningRequest = true;


            this.messages.push({
                userType: 'user',
                text: this.promptText,
                user: this.$store.state.user.user_username,
                ts: TELSA.moment().unix()
            });

            

            let self = this;
            // Make a POST request to the streaming endpoint
            fetch(`${TELSA.apiHost}ai/message`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': ('Bearer ' + localStorage.getItem('access_token'))
                },
                body: JSON.stringify({args: {interaction: self.selectedThread, newMessage: this.promptText}})
            })
            .then(response => {
                // Check if the response is OK
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                // Process the response as a stream
                const reader = response.body.getReader();

                // Read chunks of data from the stream
                function read() {
                    return reader.read().then(({ done, value }) => {
                    // Check if the stream has finished
                    if (done) {
                        console.log('Stream complete');
                        console.log(value);
                        self.runningRequest = false;
                        return;
                    }

                    let receivedData = value;
                    let message = self.buildMessageObject(receivedData);
                    if(message) {
                        self.messages.push(message);
                    }
                    self.scrollToBottom();

                    // Continue reading the stream
                    return read();
                    }).catch(error => {
                        console.error('Error reading stream:', error);
                        self.runningRequest = false;
                    });
                }

                // Start reading the stream
                return read();
            })
            .catch(error => {
                console.error('Error fetching data:', error);
                self.runningRequest = false;
            });

            this.promptText = "";
            this.scrollToBottom();
        },


        formatTextForWeb: function(text) {
            text = text.replace(/\n/g, "<br>");
            text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            return text;
        },


        buildMessageObject: function(receivedBytes) {
            console.log('receivedBytes: ', receivedBytes);
            let receivedText = new TextDecoder().decode(receivedBytes);
            console.log('receivedText: ', receivedText);
            let respObj = JSON.parse(receivedText);
            console.log('respObj: ', respObj);

            let userType = 'assistant';
            let expired = false;
            let cancelled = false;
            let user = 'TELSASOFT AI';
            let ts = TELSA.moment().unix();
            let parts = [];

            if(respObj.object == "thread") {
                this.selectedThread.thread_id = respObj.id;
                return null;
            }
            else if(respObj.object == "expired") {
                expired = true;
            }
            
            // Normal message
            if(respObj.data && respObj.data.length) {
                let data = respObj.data[0];
                for(let part of data.content) {
                    let text = null;
                    let image = null;
                    let file = null;
                    if(part.type == 'text') {
                        if(part.text.annotations && part.text.annotations.length) { // Probably a file download
                            text = part.text.value;
                            let annotation = part.text.annotations[0];
                            if(annotation.type == 'file_path') {
                                //text = (text.slice(0, annotation.start_index) + text.slice(annotation.end_index + 1));
                                text = text.replace(annotation.text, "");
                                file = annotation.file_path.file_id;
                            }
                        }
                        else {
                            // Regular textual message
                            text = part.text.value;
                        }
                    }
                    else if(part.type == 'image_file') {
                        image = part.image_file;
                    }

                    parts.push({
                        text: text,
                        image: image,
                        file: file
                    });
                }
            }
            else {
                // Empty message.
                return null;
            }

            let message = {
                userType: userType,
                expired: expired,
                cancelled: cancelled,
                user: user,
                ts: ts,
                parts: parts
            }
            return message;  
        },

        downloadFile: function(fileId) {
            console.log('Download this file: ', fileId);
            let route = (TELSA.apiHost + "ai/file/" + fileId);
            this.$http.get(route).then(response => {
                let result = response.body;

                let dlink = document.createElement('a');
                dlink.setAttribute("id", "ai-file-download");
                dlink.href = result.data_url;
                dlink.download = result.filename;
                dlink.click();
                dlink.remove();

            });
        },

        renderImage: function(image_file) {
            console.log('renderImage hit');
            if(image_file.data_url) {
                // do nothing
            }
            else if(this.gettingImageId == image_file.file_id) {
                // We are already getting this image from the server
            }
            else {
                this.gettingImageId = image_file.file_id;
                let route = (TELSA.apiHost + "ai/file/" + image_file.file_id);
                this.$http.get(route).then(response => {
                    let fileData = response.body;
                    image_file.data_url = fileData.data_url;
                    let imgContainer = document.getElementById(image_file.file_id);
                    let imgElement = document.createElement('img');
                    imgElement.src = fileData.data_url;
                    imgElement.style.width = '100%';
                    imgElement.style.height = '100%';
                    imgContainer.appendChild(imgElement);
                    this.gettingImageId = null;
                });
            }
        },

        savePromptClick: function(prompt) {
            // put in some checks here if desired
            this.savePrompt(prompt);
        },
        savePrompt: function(prompt) {
            let route = (TELSA.apiHost + 'ai/prompt/add');
            this.$http.post(route, {args: {context: this.$props.context, prompt: prompt}}).then(response => {
                let result = response.body;
                this.savedPrompts.push(result);
            });
        },

        loadPrompt: function(prompt) {
            this.promptText = prompt.prompt_text;
            document.getElementById('prompt-text-input').focus();
        },

        deletePromptClick: function(prompt) {
            this.$store.commit('showConfirmModal', {
                title: 'Are you sure?',
                message: 'Are you sure you want to delete this saved prompt?',
                type: 'warning',
                confirmAction: () => {this.deletePrompt(prompt);}
            });
        },
        deletePrompt: function(prompt) {
            let route = (TELSA.apiHost + 'ai/prompt/delete');
            this.$http.post(route, {args: {prompt_id: prompt.prompt_id}}).then(response => {
                let result = response.body;
                let idx = this.savedPrompts.indexOf(prompt);
                this.savedPrompts.splice(idx, 1);
            });
        },

        scrollToBottom: function() {
            let messagesDiv = document.getElementsByClassName('messages-area')[0];
            setTimeout(() => {
                messagesDiv.scrollTop = messagesDiv.scrollHeight;
            }, 300)
        },

        formatTS: function(unixTS) {
            return TELSA.moment.unix(unixTS).format('YYYY-MM-DD HH:mm:ss')
        },

        getUsageData: function() {
            if(this.usageData) {
                this.showUsageData = true;
                return;
            }

            this.gettingUsageData = true;
            let route = (TELSA.apiHost + 'ai/usage');
            this.$http.get(route).then(response => {
                let results = response.body;
                this.usageData = results;
                this.usageDataChartOptions = this.buildUsageChart();
                this.gettingUsageData = false;
                this.showUsageData = true;
            });
        },

        buildUsageChart: function() {
            let title = 'Telsa AI Usage';

            // Compute Data
            let categories = this.usageData.daily_costs.map(x => {
                return TELSA.moment.unix(x.timestamp).format('MM-DD');
            });

            let modelCosts = [];
            let assistantCosts = [];
            for(let day of this.usageData.daily_costs) {
                let ts = TELSA.moment.unix(day.timestamp);
                let modelCost = 0;
                let assistantCost = 0;
                for(let lineItem of day.line_items) {
                    if(lineItem.name.startsWith('GPT')) {
                        modelCost = Math.round((lineItem.cost / 100) * 100) / 100;
                    }
                    else if(lineItem.name.startsWith('Assistants')) {
                        assistantCost = Math.round((lineItem.cost / 100) * 100) / 100;
                    }
                }
                modelCosts.push(modelCost);
                assistantCosts.push(assistantCost);
            }

            let usageChartOptions = {
                chart: {
                    type: 'line',
                    backgroundColor: 'transparent',
                    zoomType: 'x',
                },
                credits: {
                    enabled: false
                },
                exporting: {
                    enabled: false
                },
                title: {
                    text: title,
                    style: {
                        color: '#ffffff'
                    }
                },
                subtitle: {
                    text: '',
                    style: {
                        color: '#ffffff'
                    }
                },

                plotOptions: {
                    column: {
                        pointPadding: 0.2,
                        borderWidth: 0
                    },
                },

                legend: {
                    itemStyle: {
                        color: '#ffffff'
                    },
                    itemHoverStyle:{
                        color: 'gray'
                    }
                },

                xAxis: {
                    //categories: dataStructureValues.map(x => {return x.categoryName}),
                    categories: categories,
                    crosshair: true,
                    labels: {
                        style: {
                            color: '#ffffff'
                        }
                    },
                },

                series: [
                    {
                        name: 'GPT-4 Turbo',
                        data: modelCosts,
                        color: '#90ED7D'
                    },
                    {
                        name: 'Assistants API',
                        data: assistantCosts,
                        color: '#7CB5EC'
                    }
                ]
            };

            return usageChartOptions;
        }
    },

    mounted() {
        this.getSavedPrompts();
    }

  
};
</script>


<style scoped>

    .close-sheet-container {
        position: absolute;
        right: 30px;
        top: 20px;
    }

    .usage-button-container {
        position: absolute;
        right: 30px;
        bottom: 20px;
    }

    .upload-file-area {
        place-items: center;
        display: grid;
        height: calc(100% - 120px);
    }

    .upload-file-container {
        max-width: 600px;
        width: 600px;
    }

    .upload-file-control {
        display: inline-block;
        width: calc(100% - 40px);
    }

    .main-message-area {
        place-items: center;
        display: grid;
        height: calc(100% - 120px);
        position: relative;
    }

    .messages-area {
        width: 1200px;
        max-width: 90%;
        height: 100%;
        overflow: auto;
        scroll-behavior: smooth;
    }






    .extra-buttons-container {
        position: absolute;
        left: 20px;
        top: 20px;
        height: 100%;
        width: 60px;
        /* border: solid 1px #747474; */
        border-radius: 5px;
        text-align: center;
        text-align: -webkit-center;
        padding: 10px;
    }

    .saved-prompt-item {
        cursor: pointer;
    }
    .saved-prompt-item:hover {
        background-color: #525252;
    }

    .delete-prompt-item-button {
        display: block;
        position: absolute;
        right: 5px;
        top: 10px;
        cursor: pointer;
    }

    .message-block {
        margin-bottom: 30px;
        animation: slide-in-bottom 0.25s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
    }

    .message-options {

    }
    .message-options .save-prompt-button {
        
    }
    .message-options .save-prompt-button:hover i {
        color: #19c37d !important;
    }

    .prompt-input-area {
        min-width: 600px;
        width: 80%;
        text-align: center;
        display: block;
        position: absolute;
        bottom: 10px;
        left: calc(20% / 2)
    }

    .prompt-input {
        max-width: 600px;
        display: block;
        text-align: center;
        position: relative;
        left: calc(50% - (600px / 2));
    }

    .user-image {
        width: 50px;
        height: 50px;
        box-shadow: 3px 3px 8px 0px rgba(0,0,0,0.3);
    }

    .user-icon {
        font-size: 50px;
        box-shadow: 3px 3px 8px 0px rgba(0,0,0,0.3);
    }

    .expired-message-tag {
        max-width: 200px;
        width: 200px;
        height: 36px;
        background-color: rgba(255, 82, 82, 0.8);
        border-radius: 5px;
        border: gray solid 1px;
        text-align: center;
    }
    .expired-message-tag span {
        display: block;
        color: #FFFFFF;
        /* font-weight: bold; */
        margin-top: 8px;
    }


    .disclaimer-text-container {
        min-width: 420px;
        width: 420px;
        text-align: center;
        display: block;
        position: absolute;
        bottom: 8px;
        left: calc(50% - (420px / 2));
    }
    .disclaimer-text-container span {
        color: rgba(255,255,255,0.8);
    }


    @keyframes slide-in-bottom {
        0% {
            transform: translateY(1000px);
            opacity: 0;
        }
        100% {
            transform: translateY(0);
            opacity: 1;
        }
    }

    .usage-totals-container {
        display: block;
        position: absolute;
        background-color: #303030;
        padding: 6px;
        border-radius: 4px;
        width: 240px;
        left: calc(50% - (240px / 2));
        bottom: 25px;
    }

    .getting-usage-data {
        display: block;
        font-size: 26px;
        top: 100px;
        position: absolute;
        width: 280px;
        left: calc(50% - (280px / 2));
    }

</style>







<style scoped>

    .running-request-indicator-area {
        display: block;
        position: absolute;
        width: 60px;
        height: 60px;
        text-align: center;
        left: calc(50% - (80px / 2));
        bottom: 100px;

        animation: scale-in-center 0.5s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
    }

    .loader {
        position: relative;
        top: calc(50% - 19px);
        left: calc(50% - 19px);
        width: 38px;
        height: 38px;
        border-radius: 50%;
        perspective: 800px;
    }

    .inner {
        position: absolute;
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        border-radius: 50%;
    }

    .inner.one {
        left: 0%;
        top: 0%;
        animation: rotate-one 1s linear infinite;
        border-bottom: 3px solid #24D4DD;
    }

    .inner.two {
        right: 0%;
        top: 0%;
        animation: rotate-two 1s linear infinite;
        border-right: 3px solid #943AE8;
    }

    .inner.three {
        right: 0%;
        bottom: 0%;
        animation: rotate-three 1s linear infinite;
        border-top: 3px solid #EAA015;
    }

    @keyframes rotate-one {
        0% {
            transform: rotateX(35deg) rotateY(-45deg) rotateZ(0deg);
        }
        100% {
            transform: rotateX(35deg) rotateY(-45deg) rotateZ(360deg);
        }
    }

    @keyframes rotate-two {
        0% {
            transform: rotateX(50deg) rotateY(10deg) rotateZ(0deg);
        }
        100% {
            transform: rotateX(50deg) rotateY(10deg) rotateZ(360deg);
        }
    }

    @keyframes rotate-three {
        0% {
            transform: rotateX(35deg) rotateY(55deg) rotateZ(0deg);
        }
        100% {
            transform: rotateX(35deg) rotateY(55deg) rotateZ(360deg);
        }
    }

    @keyframes scale-in-center {
        0% {
            transform: scale(0);
            opacity: 1;
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    }


</style>

